/**
 * Hook to create and manage ChartStore instance lifecycle.
 * Returns a stable ref to avoid triggering re-renders.
 */

import { useEffect, useRef } from "react";

import { createChartStore } from "~utils/ticker-chart";
import { type ChartStore, type ChartStoreConfig } from "~types/types";

/**
 * Create a ChartStore instance with stable reference.
 * The store persists for the component lifecycle.
 * Automatically destroys the store on unmount.
 * Detects config changes and updates store accordingly.
 */
export function useChartStore(config: ChartStoreConfig): ChartStore {
  const storeRef = useRef<ChartStore | null>(null);
  const configRef = useRef(config);

  storeRef.current = storeRef.current ?? createChartStore(config);

  // Detect config changes and update store
  useEffect(() => {
    const store = storeRef.current;

    if (!store) return;

    const prevConfig = configRef.current;

    // Update series data if changed
    if (prevConfig.series !== config.series) {
      config.series.forEach((series) => {
        store.setSeriesData(series.id, series.priceMovements);
      });
    }

    // Update dimensions if changed
    if (
      prevConfig.width !== config.width ||
      prevConfig.height !== config.height
    ) {
      store.setDimensions(
        typeof config.width === "number" ? config.width : 800,
        config.height
      );
    }

    configRef.current = config;
  }, [config]);

  useEffect(() => {
    return () => {
      storeRef.current?.destroy();
      storeRef.current = null;
    };
  }, []);

  return storeRef.current;
}
